import React from "react";
import {
  Box, Typography, Chip,
  Select, MenuItem, Checkbox, ListItemText,
} from "@mui/material";
import { RESPONSE_FORMATS, EMPTY_RESPONSE_PREFERENCES, LIST_STYLES } from "../constants";
import {
  MessageText as MessageTextIconIx, Element3 as Element3IconIx,
  MessageTick as MessageTickIconIx, ShoppingBag as ShoppingBagIconIx,
} from "iconsax-react";
import PillSwitch from "./PillSwitch";
import { LabeledField, inboxSelectSx, inboxSelectMenuProps, inboxMenuItemSx } from "./LabeledField";

// Per-format glyph, same icon-pill convention as this page's own Knowledge
// Sources pills (Rahim, 2026-09-03: "add icons ... add it in a pill ...
// kinda like the knowledge source") — swapped in for Allowed formats'
// collapsed renderValue, which was just a comma-joined label string
// before. Plain neutral grey, not the colorful per-type accents Knowledge
// Sources uses (Rahim: "dont use coloured one use light grey colour") —
// these 4 formats aren't distinct categories the way source file types
// are, so a flat neutral pill reads calmer for a value that's just
// restating the Select's own selection back to you.
const FORMAT_TYPE_ICONS = {
  text: MessageTextIconIx,
  list: Element3IconIx,
  buttons: MessageTickIconIx,
  catalog: ShoppingBagIconIx,
};
const formatPillSx = (theme) => ({
  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.045)",
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "6px",
  fontWeight: 600,
  height: 22,
  fontSize: "0.6875rem",
  "& .MuiChip-label": { px: 0.75 },
  "& .MuiChip-icon": { color: "inherit", ml: "6px", fontSize: "12px" },
});

/**
 * Controls agent.useStructuredPlanner + agent.responsePreferences (see
 * responsePolicy.service.js / semanticPlanner.service.js on the server) — previously
 * only settable via a direct API call or script, with no UI at all.
 */
export default function ResponseFormatSection({ form, setForm }) {
  const rp = form.responsePreferences || EMPTY_RESPONSE_PREFERENCES;

  const setRP = (patch) =>
    setForm((prev) => ({
      ...prev,
      responsePreferences: { ...(prev.responsePreferences || EMPTY_RESPONSE_PREFERENCES), ...patch },
    }));

  const toggleAllowedFormat = (value) => {
    const current = rp.allowedFormats?.length ? rp.allowedFormats : EMPTY_RESPONSE_PREFERENCES.allowedFormats;
    const next = current.includes(value)
      ? current.filter((f) => f !== value)
      : [...current, value];
    // "text" is always feasible on every channel and is the universal fallback —
    // never let it be turned off, or every option-bearing reply would have nowhere
    // to fall back to when list/buttons/catalog don't fit.
    setRP({ allowedFormats: next.includes("text") ? next : [...next, "text"] });
  };

  return (
    // gap:2 between every group (heading / toggle+caption / toggle+caption
    // / selects row / list-style+caption) instead of scattered one-off mt
    // values on each element.
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Restructured to match SectionCard's actual layout mechanism
          (Rahim, 2026-09-03, after hand-tuned pl/mt patches still didn't
          read right): icon is a flex-start sibling of a nested column
          holding heading+description together (gap:0.5, identical to
          SectionCard's own) — indent and spacing both fall out of the
          layout naturally instead of being computed by hand. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box
          sx={{
            width: 32, height: 32, flexShrink: 0,
            borderRadius: "8px",
            border: "1px solid", borderColor: "divider",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "text.primary",
          }}
        >
          <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MessageTextIconIx size="100%" color="currentColor" variant="Linear" />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>Message Format</Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
            Control how the agent structures and sends its replies.
          </Typography>
        </Box>
      </Box>

      {/* Toggle moved to the right side of its own row (Rahim, 2026-09-03:
          "make the toggle to the right side of the container just like u
          did for the office hours") — same title+switch row / caption
          below shape as Office Hours' Enabled/Disabled toggle, replacing
          the FormControlLabel-as-one-unit approach. Captions shortened too
          (Rahim: "make the sub para text less"). */}
      {/* Toggle aligns with the CAPTION row, not the title row (Rahim,
          2026-09-03: "make sure to align it not with the heading but the
          sub para") — title sits alone on top; the row below holds the
          caption + toggle together, space-between, exactly like Office
          Hours' own heading / description+toggle split. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>
          Use structured response planner (recommended)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            Structures replies as data instead of raw text. Off falls back to legacy free-text.
          </Typography>
          <PillSwitch
            checked={form.useStructuredPlanner !== false}
            onChange={(e) => setForm((prev) => ({ ...prev, useStructuredPlanner: e.target.checked }))}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>
          Send interactive lists/buttons for AI-detected options
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            When off, AI-suggested choices render as plain text instead of tappable buttons.
          </Typography>
          <PillSwitch
            checked={!!rp.autoFormatNonTaskReplies}
            onChange={(e) => setRP({ autoFormatNonTaskReplies: e.target.checked })}
          />
        </Box>
      </Box>

      {/* Rahim, 2026-09-03: "the drop downs ... the text for the whole
          container is not same as above cards" — these 3 Selects had zero
          custom styling (plain MUI default 4px radius + floating
          InputLabel), the one spot on this page still off the
          LabeledField (label above the box) + inboxSelectSx (8px radius,
          divider border) convention every other field here already uses.
          inboxSelectMenuProps/inboxMenuItemSx match the same Inbox-style
          dropdown convention this page's own Knowledge Sources picker
          already uses — rounded-inset hover rows instead of MUI's stock
          green Mui-selected background. A size="medium" pass tried making
          the collapsed box bigger too; reverted — "make the dropdwon
          small its soo big very weird" — size="small" matches every other
          field on this page.
          All 3 selects now share ONE row, equal fixed width, no flex:1 on
          any of them (Rahim, 2026-09-03: "two are of small width and one
          is of long ... giving access space to these two for no reason")
          — Allowed formats used to stretch via flex:1 while Options list
          style sat stranded alone on its own row with dead space beside
          it; now all three align evenly instead. Options list style's
          caption moved from a full-width line below everything into that
          field's own column (still directly under it, just wrapped to the
          column's width instead of the whole page). */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* width, not minWidth — a long "Allowed formats" renderValue
            string (e.g. all 4 formats) grew that Select past minWidth's
            floor with nothing capping it, pushing the total row past the
            container and wrapping Options list style onto its own line
            anyway (the exact thing this restructure was meant to fix).
            width+fullWidth actually caps it; long values ellipsis-truncate
            inside the fixed box instead of expanding it. */}
        <LabeledField label="Preferred format" sx={{ width: 220 }}>
          <Select
            fullWidth
            size="small"
            value={rp.defaultFormat || "text"}
            onChange={(e) => setRP({ defaultFormat: e.target.value })}
            MenuProps={inboxSelectMenuProps}
            sx={inboxSelectSx}
          >
            {RESPONSE_FORMATS.map((f) => (
              <MenuItem key={f.value} value={f.value} sx={inboxMenuItemSx}>{f.label}</MenuItem>
            ))}
          </Select>
        </LabeledField>

        {/* flex:1 — Rahim, 2026-09-03: "make the allowed formats width big
            so the options list style is touching the most right side of
            the card border". Safe to flex now (unlike the original
            attempt): the Select is `fullWidth` inside this container, so
            it fills whatever space flex gives it and ellipsis-truncates
            its own long value, instead of the container growing to match
            the Select's content like before. */}
        <LabeledField label="Allowed formats" sx={{ flex: 1 }}>
          <Select
            fullWidth
            multiple
            size="small"
            value={rp.allowedFormats?.length ? rp.allowedFormats : EMPTY_RESPONSE_PREFERENCES.allowedFormats}
            onChange={(e) => setRP({ allowedFormats: e.target.value })}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {RESPONSE_FORMATS.filter((f) => selected.includes(f.value)).map((f) => {
                  const FormatIcon = FORMAT_TYPE_ICONS[f.value];
                  return (
                    <Chip
                      key={f.value}
                      icon={<FormatIcon size={12} variant="Bold" />}
                      label={f.label}
                      sx={formatPillSx}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={inboxSelectMenuProps}
            sx={inboxSelectSx}
          >
            {RESPONSE_FORMATS.map((f) => (
              <MenuItem
                key={f.value} value={f.value}
                onClick={(e) => { e.preventDefault(); toggleAllowedFormat(f.value); }}
                sx={(theme) => ({ ...inboxMenuItemSx(theme), gap: 1 })}
              >
                {/* size="small" alone still left a 35px touch-target box
                    (confirmed via computed height — that's what was
                    forcing every row identical regardless of text,
                    outliving 3 separate earlier attempts at this).
                    sx={{p:0}} is the actual fix, matching the Knowledge
                    Sources picker dialog's own checkbox exactly (that one
                    never had this problem because it already carries
                    both). */}
                <Checkbox
                  size="small"
                  sx={{ p: 0 }}
                  checked={(rp.allowedFormats?.length ? rp.allowedFormats : EMPTY_RESPONSE_PREFERENCES.allowedFormats).includes(f.value)}
                  disabled={f.value === "text"}
                />
                {/* my:0 — ListItemText carries its own default ~4px
                    top/bottom margin completely independent of the row's
                    own padding, which is what was still forcing the
                    single-line rows (List message/Quick-reply buttons)
                    tall even after the checkbox and row py were both
                    already shrunk (Rahim, 2026-09-03, 2nd pass: "still soo
                    damn massive"). Line-height tightened too so one line
                    of text doesn't reserve more vertical space than it
                    needs. "Plain text" genuinely has 2 lines (its own
                    caption) so it stays proportionally taller — correct,
                    not a bug. */}
                <ListItemText
                  primary={f.label}
                  secondary={f.value === "text" ? "Always allowed" : undefined}
                  sx={{ my: 0 }}
                  primaryTypographyProps={{ fontSize: "0.9375rem", lineHeight: 1.3 }}
                  secondaryTypographyProps={{ fontSize: "0.75rem", lineHeight: 1.3 }}
                />
              </MenuItem>
            ))}
          </Select>
        </LabeledField>

        {/* Renamed + shortened caption (Rahim, 2026-09-03: "the bottom sub
            para looks weird ... use a better and easy name so its obvious
            what the field is for") — "Options list style (plain text)"
            buried the actual point (this is about the LIST format) behind
            an awkward parenthetical, and its old caption wrapped 3 lines
            deep in this narrow column. "Plain Text List Format" says what
            it does up front; the caption now just adds the one detail the
            label doesn't already cover. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: 220 }}>
          <LabeledField label="Plain Text List Format">
            <Select
              fullWidth
              size="small"
              value={rp.listStyle || (rp.bullets === false ? "flowing" : "bullet")}
              onChange={(e) => setRP({ listStyle: e.target.value })}
              MenuProps={inboxSelectMenuProps}
              sx={inboxSelectSx}
            >
              {LIST_STYLES.map((s) => (
                <MenuItem key={s.value} value={s.value} sx={inboxMenuItemSx}>{s.label}</MenuItem>
              ))}
            </Select>
          </LabeledField>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            Used when a reply can't be a tappable WhatsApp list or buttons.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
